import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { toFilterOptions } from '@util/filter'

import { useNavigate } from '../../../router/hooks/useNavigate'
import { useStatusConfig } from '../../../status/hooks/useStatusConfig'
import { Page } from '../../../theme/components/Page'
import { PageSection } from '../../../theme/components/PageSection'
import { useHoofdleidingValuelists } from '../../../valuelists/hooks/useHoofdleidingValuelists'
import { useValuelists } from '../../../valuelists/hooks/useValuelists'
import { useActiviteitenSearchStore } from '../../activiteiten/hooks/useActiviteitenSearchStore'
import { useAssignedBijlagenSearchStore } from '../../bijlagen/hooks/useAssignedBijlagenSearchStore'
import { useProjectenSearchStore } from '../../projecten/hooks/useProjectenSearchStore'
import { useSubprocessenSearchStore } from '../../subprocessen/hooks/useSubprocessenSearchStore'
import { Processtappen } from '../components/Processtappen'
import { Subprocessen } from '../components/Subprocessen'
import { TeBeoordelen } from '../components/TeBeoordelen'
import { Werklijsten } from '../components/Werklijsten'
import { useSelectedTabStore } from '../hooks/useSelectedTabStore'
import { useSubprocessen } from '../hooks/useSubprocessen'
import type {
  DashboardNavigation,
  DashboardQueries,
} from '../types/DashboardNavigation'

export const Dashboard = () => {
  const navigate = useNavigate()
  const { data: valuelists } = useValuelists()
  const { data: hoofleidingValuelists } = useHoofdleidingValuelists()
  const { data: statusconfig } = useStatusConfig()
  const {
    data: { subprocessen },
  } = useSubprocessen()

  const canViewSubprocessen = !!subprocessen.length

  const { selectedTab, storeSelectedTab } = useSelectedTabStore()
  const { storeQuery: storeQueryActiviteiten, query: queryActiviteiten } =
    useActiviteitenSearchStore()
  const {
    storeQuery: storeQueryAssignedBijlagen,
    query: queryAssignedBijlagen,
  } = useAssignedBijlagenSearchStore()

  const { storeQuery: storeQueryHoofdleidingen } = useProjectenSearchStore()
  const { storeQuery: storeQuerySubprocessen } = useSubprocessenSearchStore()

  const storeQueries: {
    [U in keyof DashboardQueries]: (query: DashboardQueries[U]) => void
  } = {
    '/hoofdleiding/projecten': storeQueryHoofdleidingen,
    '/hoofdleiding/subprocessen': storeQuerySubprocessen,
    '/hoofdleiding/bijlagen': (query) =>
      storeQueryAssignedBijlagen({
        ...query,
        sort: queryAssignedBijlagen.sort,
      }),
    '/hoofdleiding/activiteiten': (query) =>
      storeQueryActiviteiten({ ...query, sort: queryActiviteiten.sort }),
  }

  const onClickTile = <U extends keyof DashboardQueries>({
    url,
    query,
  }: DashboardNavigation<U>) => {
    storeQueries[url](query)
    navigate(url)
  }

  return (
    <Page
      title={['Dashboard', 'Hoofdleiding']}
      breadcrumbs={[
        { label: 'Hoofdleiding', href: '/home', tooltip: 'CAPO home' },
        { label: 'Dashboard' },
      ]}
      fillScreen
    >
      <PageSection id="dashboard" heading="Dashboard" />

      <Tabs
        centered
        value={selectedTab}
        onChange={(_, tab) => storeSelectedTab(tab)}
      >
        <Tab label="Processtappen" value="processtappen" />
        <Tab label="Werklijsten" value="werklijsten" />
        <Tab label="Te beoordelen" value="te_beoordelen" />
        {canViewSubprocessen && (
          <Tab label="Subprocessen" value="subprocessen" />
        )}
      </Tabs>

      <Paper>
        {selectedTab === 'processtappen' && (
          <Processtappen
            onClickTile={onClickTile}
            filterOptions={{
              ...valuelists,
              hoofdleiding: hoofleidingValuelists,
              labels: toFilterOptions(hoofleidingValuelists.labels),
            }}
          />
        )}
        {selectedTab === 'werklijsten' && (
          <Werklijsten
            onClickTile={onClickTile}
            filterOptions={{
              ...valuelists,
              hoofdleiding: hoofleidingValuelists,
              labels: toFilterOptions(hoofleidingValuelists.labels),
              processtappen: statusconfig.processtappenHoofdleiding,
            }}
          />
        )}
        {selectedTab === 'te_beoordelen' && (
          <TeBeoordelen
            onClickTile={onClickTile}
            filterOptions={{
              ...valuelists,
              hoofdleiding: hoofleidingValuelists,
              labels: toFilterOptions(hoofleidingValuelists.labels),
              processtappen: statusconfig.processtappenHoofdleiding,
            }}
          />
        )}
        {selectedTab === 'subprocessen' && (
          <Subprocessen
            onClickTile={onClickTile}
            filterOptions={{
              ...valuelists,
              hoofdleiding: hoofleidingValuelists,
              labels: toFilterOptions(hoofleidingValuelists.labels),
              processtappen: statusconfig.processtappenHoofdleiding,
            }}
          />
        )}
      </Paper>
    </Page>
  )
}
